import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { type OrganizationEvent } from '@apps/web/hooks/use_organization_events';
import { toast } from '@apps/web/lib/app_notifications';
import { useMode } from '@apps/web/lib/mode_store';
import {
    dismissRecommendationCaregiver,
    type RecommendationUnitType,
    stageRecommendationCaregiver,
    useOutreachCaregiverSelections,
    withdrawRecommendationCaregiver,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type OutreachCaregiverSelectionRead } from '@libraries/typescript/api_client';

export type OutreachSelectionAttribution = {
    readonly actor: OutreachCaregiverSelectionRead['status_changed_by'];
    readonly changedAt: string;
};

export type OutreachV3SelectionState = {
    readonly isReady: boolean;
    readonly stagedCaregiverIds: ReadonlySet<string>;
    readonly pinnedCaregiverIds: ReadonlySet<string>;
    readonly attributionByCaregiverId: ReadonlyMap<
        string,
        OutreachSelectionAttribution
    >;
    readonly persistSelectionDelta: (delta: {
        readonly addedIds: ReadonlyArray<string>;
        readonly removedIds: ReadonlyArray<string>;
    }) => void;
};

const EMPTY_SELECTIONS: ReadonlyArray<OutreachCaregiverSelectionRead> = [];
const EMPTY_OVERRIDES: ReadonlyMap<string, boolean> = new Map();
const SELECTION_EVENT_THROTTLE_MS = 3000;

function selectionMatchesUnit(
    selection: OutreachCaregiverSelectionRead,
    unitType: RecommendationUnitType,
    unitId: string,
) {
    return unitType === 'shift_group'
        ? selection.group_id === unitId
        : selection.shift_id === unitId;
}

/**
 * The v3-born caregiver table's checkbox state for one recommendation unit.
 *
 * The server's selection rows are the truth and the checkbox is derived from
 * them, so a repaint driven by the runner or another coordinator lands on an
 * open page. `stagedOverrides` holds only the deltas a write has not been
 * confirmed for yet: each one is dropped the moment fresh rows agree with it,
 * so a slow response can never invert a newer server state, and a failed
 * write rolls back exactly the ids that failed.
 *
 * Unchecking dismisses a suggestion the agent staged (the coordinator is
 * overruling Phoebe, which the agent must see) but withdraws one a
 * coordinator staged. `locallyStagedIdsRef` carries that signal for a row
 * this session just staged, because the server rows still describe the
 * pre-stage world until the refetch lands.
 */
export function useOutreachV3SelectionState({
    organizationId,
    outreachId,
    unitType,
    unitId,
    enabled,
}: {
    readonly organizationId: string | null;
    readonly outreachId: string | null | undefined;
    readonly unitType: RecommendationUnitType;
    readonly unitId: string | null;
    readonly enabled: boolean;
}): OutreachV3SelectionState {
    const mode = useMode();
    const { data, error, mutate } = useOutreachCaregiverSelections(
        organizationId,
        outreachId,
        { reason: 'inhouse-recommendations-table-v3-selections' },
        { enabled },
    );
    const [stagedOverrides, setStagedOverrides] =
        useState<ReadonlyMap<string, boolean>>(EMPTY_OVERRIDES);
    const locallyStagedIdsRef = useRef<Set<string>>(new Set());

    const handleSelectionEvent = useCallback(
        (event: OrganizationEvent) => {
            if (
                event.resource.type === 'outreach' &&
                event.resource.id === outreachId
            ) {
                void mutate();
            }
        },
        [mutate, outreachId],
    );
    // Selection rows change under two different signals and neither covers
    // the other: the REST selection routes take their own lock and publish
    // only the suggestion events, while a write through the outreach lock
    // (adding shifts propagates a group's dismissals onto them) publishes
    // only `outreach_updated`.
    useListenForOrganizationEvent(
        'outreach_suggestion_added',
        handleSelectionEvent,
        { organizationId, throttleMs: SELECTION_EVENT_THROTTLE_MS },
    );
    useListenForOrganizationEvent(
        'outreach_suggestion_removed',
        handleSelectionEvent,
        { organizationId, throttleMs: SELECTION_EVENT_THROTTLE_MS },
    );
    useListenForOrganizationEvent('outreach_updated', handleSelectionEvent, {
        organizationId,
        throttleMs: SELECTION_EVENT_THROTTLE_MS,
    });

    const unitSelections = useMemo(() => {
        if (data === undefined || unitId === null) {
            return EMPTY_SELECTIONS;
        }
        return data.selections.filter((selection) =>
            selectionMatchesUnit(selection, unitType, unitId),
        );
    }, [data, unitId, unitType]);

    const serverStagedCaregiverIds = useMemo(
        () =>
            new Set(
                unitSelections
                    .filter((selection) => selection.status === 'staged')
                    .map((selection) => selection.caregiver_id),
            ),
        [unitSelections],
    );

    // Who put each row into STAGED — the exact question "did the agent
    // suggest this, or did a coordinator pick it?" asks.
    const stagerByCaregiverId = useMemo(
        () =>
            new Map(
                unitSelections
                    .filter((selection) => selection.status === 'staged')
                    .map((selection) => [
                        selection.caregiver_id,
                        selection.status_changed_by,
                    ]),
            ),
        [unitSelections],
    );

    const scopeKey = `${mode}:${outreachId ?? ''}:${unitType}:${unitId ?? ''}`;
    useEffect(() => {
        locallyStagedIdsRef.current = new Set();
        setStagedOverrides((previousOverrides) =>
            previousOverrides.size === 0 ? previousOverrides : EMPTY_OVERRIDES,
        );
    }, [scopeKey]);

    // Reconcile against every arrival of fresh rows: an override survives
    // only while the server still disagrees with it, so a response that
    // predates the write cannot clear it.
    useEffect(() => {
        for (const caregiverId of [...locallyStagedIdsRef.current]) {
            if (stagerByCaregiverId.get(caregiverId) === 'user') {
                locallyStagedIdsRef.current.delete(caregiverId);
            }
        }
        setStagedOverrides((previousOverrides) => {
            if (previousOverrides.size === 0) {
                return previousOverrides;
            }
            const nextOverrides = new Map(previousOverrides);
            for (const [caregiverId, isStaged] of previousOverrides) {
                if (serverStagedCaregiverIds.has(caregiverId) === isStaged) {
                    nextOverrides.delete(caregiverId);
                }
            }
            return nextOverrides.size === previousOverrides.size
                ? previousOverrides
                : nextOverrides;
        });
    }, [serverStagedCaregiverIds, stagerByCaregiverId]);

    const stagedCaregiverIds = useMemo(() => {
        if (stagedOverrides.size === 0) {
            return serverStagedCaregiverIds;
        }
        const ids = new Set(serverStagedCaregiverIds);
        for (const [caregiverId, isStaged] of stagedOverrides) {
            if (isStaged) {
                ids.add(caregiverId);
            } else {
                ids.delete(caregiverId);
            }
        }
        return ids;
    }, [serverStagedCaregiverIds, stagedOverrides]);

    const pinnedCaregiverIds = useMemo(
        () =>
            new Set(
                unitSelections
                    .filter((selection) => selection.pinned)
                    .map((selection) => selection.caregiver_id),
            ),
        [unitSelections],
    );

    const attributionByCaregiverId = useMemo(
        () =>
            new Map(
                unitSelections.map((selection) => [
                    selection.caregiver_id,
                    {
                        actor: selection.status_changed_by,
                        changedAt: selection.status_changed_at,
                    },
                ]),
            ),
        [unitSelections],
    );

    const persistSelectionDelta = useCallback(
        ({
            addedIds,
            removedIds,
        }: {
            readonly addedIds: ReadonlyArray<string>;
            readonly removedIds: ReadonlyArray<string>;
        }) => {
            if (
                !organizationId ||
                !outreachId ||
                unitId === null ||
                (addedIds.length === 0 && removedIds.length === 0)
            ) {
                return;
            }
            setStagedOverrides((previousOverrides) => {
                const nextOverrides = new Map(previousOverrides);
                for (const caregiverId of addedIds) {
                    nextOverrides.set(caregiverId, true);
                }
                for (const caregiverId of removedIds) {
                    nextOverrides.set(caregiverId, false);
                }
                return nextOverrides;
            });

            const request = { organizationId, outreachId, unitType, unitId };
            const writes = [
                ...addedIds.map((caregiverId) => {
                    locallyStagedIdsRef.current.add(caregiverId);
                    return {
                        caregiverId,
                        staged: true,
                        promise: stageRecommendationCaregiver({
                            ...request,
                            caregiverId,
                        }),
                    };
                }),
                ...removedIds.map((caregiverId) => {
                    const stagedByCoordinator =
                        locallyStagedIdsRef.current.has(caregiverId) ||
                        stagerByCaregiverId.get(caregiverId) === 'user';
                    locallyStagedIdsRef.current.delete(caregiverId);
                    return {
                        caregiverId,
                        staged: false,
                        promise: stagedByCoordinator
                            ? withdrawRecommendationCaregiver({
                                  ...request,
                                  caregiverId,
                              })
                            : dismissRecommendationCaregiver({
                                  ...request,
                                  caregiverId,
                              }),
                    };
                }),
            ];

            void Promise.allSettled(writes.map((write) => write.promise)).then(
                (results) => {
                    const failed = writes.filter(
                        (_write, index) =>
                            results[index]?.status === 'rejected',
                    );
                    if (failed.length > 0) {
                        setStagedOverrides((previousOverrides) => {
                            const nextOverrides = new Map(previousOverrides);
                            for (const write of failed) {
                                nextOverrides.delete(write.caregiverId);
                                if (write.staged) {
                                    locallyStagedIdsRef.current.delete(
                                        write.caregiverId,
                                    );
                                }
                            }
                            return nextOverrides;
                        });
                        toast.error(
                            'Could not save caregiver selection changes.',
                            {
                                persistNotification: false,
                            },
                        );
                    }
                    void mutate();
                },
            );
        },
        [
            mutate,
            organizationId,
            outreachId,
            stagerByCaregiverId,
            unitId,
            unitType,
        ],
    );

    // A fresh object here would change identity on every render of every
    // table, rebuilding the memoized columns and remounting each header —
    // the WEB-CS update-depth cascade. Every member below is already
    // memoized or a primitive, so this stays stable between real changes.
    const isReady = !enabled || data !== undefined || error !== undefined;
    return useMemo(
        () => ({
            isReady,
            stagedCaregiverIds,
            pinnedCaregiverIds,
            attributionByCaregiverId,
            persistSelectionDelta,
        }),
        [
            attributionByCaregiverId,
            isReady,
            persistSelectionDelta,
            pinnedCaregiverIds,
            stagedCaregiverIds,
        ],
    );
}
